import { TokenService } from '@admin/core/services/token.service';
import { UserService } from '@admin/states';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { Menu, Privilege, AccessGroupList, User } from '@admin/models';
import { MENUS } from './menu';
import { DialogService } from '@admin/core/services/dialog.service';
import { PendingApprovalCountsService } from '@admin/core/services/pending-approval-counts.service';
import { UserApiService } from '@admin/core/services/user-api.service';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from '@admin/environments';
import { PendingApprovalCounts } from '@ngx-hal/web-services';

/**
 * Represents business line privileges mapped by business line codes
 * @interface BusinessType
 * @example
 * ```typescript
 * const businessType: BusinessType = {
 *   'tc': { view: true, insert: false, update: true, delete: false },
 *   'tk': { view: true, insert: true, update: true, delete: true }
 * };
 * ```
 */
export interface BusinessType {
  [key: string]: Privilege;
}

/**
 * Represents resource types mapped by resource names containing business types
 * @interface ResourceType
 * @example
 * ```typescript
 * const resourceType: ResourceType = {
 *   'tyres': {
 *     'tc': { view: true, insert: false, update: true, delete: false }
 *   },
 *   'vehicles': {
 *     '4w': { view: true, insert: true, update: true, delete: true }
 *   }
 * };
 * ```
 */
export interface ResourceType {
  [key: string]: BusinessType;
}

/**
 * Main layout component that handles application structure, authentication, and navigation.
 * This component manages user authentication, permissions, menu filtering, and data loading states.
 *
 * @description
 * The LayoutComponent serves as the main wrapper component that:
 * - Handles Auth0 authentication flow with token management
 * - Manages user permissions and access control based on access groups
 * - Filters navigation menus based on user privileges and business lines
 * - Monitors route changes and displays loading states
 * - Manages error states and user session lifecycle
 * - Processes regional and outsourcing group permissions
 *
 * @component LayoutComponent
 * @selector datamint-vehicles-browser-admin-layout
 *
 * @example
 * ```html
 * <datamint-vehicles-browser-admin-layout></datamint-vehicles-browser-admin-layout>
 * ```
 *
 * ## Class Properties:
 *
 * ### State Management Properties:
 * - `errorMessage$` - BehaviorSubject for error message state management
 * - `dataLoaded$` - BehaviorSubject tracking data loading completion
 * - `appState$` - Combined observable of authentication and application state
 * - `routeChangeLoader$` - Observable that tracks route navigation loading state
 *
 * ### Menu & Navigation Properties:
 * - `menus` - Array of filtered menu items based on user permissions
 * - `currentYear` - Current year for copyright display
 *
 * ### User Information Properties:
 * - `username` - Current authenticated user's username (email)
 * - `email` - Current authenticated user's email address
 * - `isUserPriviledge` - Flag indicating if user has required access privileges
 *
 * ### Template References:
 * - `errorDialogRef` - ViewChild reference to error dialog template
 *
 * ## Constructor Dependencies:
 * - `tokenService` - Service for managing Auth0 authentication tokens
 * - `userService` - Service for managing user data, permissions, and session state
 * - `route` - Angular Router service for navigation and redirects
 * - `dialogService` - Service for managing modal dialogs and overlays
 * - `userApiService` - API service for fetching user details and regional data
 * - `cdr` - ChangeDetectorRef for manual change detection optimization
 * - `authService` - Auth0 authentication service for login/logout operations
 *
 * @security
 * - All menu filtering is based on server-provided access groups
 * - Client-side filtering is for UX only; server-side validation is required
 * - Tokens are managed securely through Auth0 service with automatic refresh
 * - Session data is cleared on authentication errors or logout
 *
 * ## Authentication Flow:
 * 1. Monitor token changes through tokenService.accessTokenObservable
 * 2. Fetch user details from API when authenticated
 * 3. Process access groups and create privilege mappings
 * 4. Filter menus based on user permissions
 * 5. Handle post-authentication redirects
 * 6. Manage error states and automatic logout on failures
 */
@Component({
  selector: 'datamint-vehicles-browser-admin-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LayoutComponent implements OnInit{
  private readonly userService = inject(UserService);
  private readonly pendingApprovalCountsService = inject(PendingApprovalCountsService);
  protected errorMessage$: BehaviorSubject<{ message: string } | null> = new BehaviorSubject<{
    message: string;
  } | null>(null);
  protected menus: Menu[] = MENUS;
  protected pendingCounts$: Observable<PendingApprovalCounts | null> = this.pendingApprovalCountsService.pendingCounts$;
  protected username?: string;
  protected email?: string;
  protected environmentName?: string;
  protected isUserPriviledge = true;
  protected currentYear: number = new Date().getFullYear();
  @ViewChild('errorDialogRef') errorDialogRef?: TemplateRef<unknown>;
  protected dataLoaded$ = new BehaviorSubject<boolean>(false);
  protected ramuPortalUrl$ = this.tokenService.accessTokenObservable.pipe(
    map(token => {
      const base = `${environment.auth0.ramuUrl}/dashboard`;
      return token ? `${base}?token=${token}` : base;
    }),
  );

  // Combined authentication and app state
  protected appState$ = combineLatest([
    this.tokenService.accessTokenObservable,
    this.userService.user$,
    this.dataLoaded$,
    this.authService.isLoading$,
  ]).pipe(
    map(([token, user, dataLoaded, isAuthLoading]) => ({
      isLoggedIn: !!token,
      hasPermissions: !!user,
      dataLoaded,
      isReady: !!token && !!user && dataLoaded,
      isAuthLoading: isAuthLoading,
      showLoginButton: !isAuthLoading && !token,
    })),
    shareReplay(1),
  );

  // Route change loader
  protected routeChangeLoader$ = this.route.events.pipe(
    filter(
      e =>
        e instanceof NavigationStart ||
        e instanceof NavigationEnd ||
        e instanceof NavigationCancel ||
        e instanceof NavigationError,
    ),
    map(e => e instanceof NavigationStart),
  );

  /**
   * Initializes the LayoutComponent and sets up the complete authentication and user management flow
   *
   * @description
   * The constructor orchestrates the application's initialization sequence:
   *
   * **Authentication Flow:**
   * 1. Subscribes to token changes from Auth0 service
   * 2. When token is available, fetches user profile from Auth0
   * 3. Sets authentication data in user service
   * 4. Calls API to get detailed user permissions and access groups
   *
   * **User Data Processing:**
   * 1. Sets user data in user service for global access
   * 2. Processes regional permissions (fetchCountryIds)
   * 3. Extracts outsourcing group permissions
   * 4. Filters navigation menus based on user access groups
   * 5. Creates privilege mapping for fine-grained permission checking
   *
   * **Post-Authentication:**
   * 1. Handles stored redirect URLs from pre-auth navigation attempts
   * 2. Marks data as loaded for UI state management
   * 3. Triggers change detection for immediate UI updates
   *
   * **Error Handling:**
   * - API failures trigger error dialogs with automatic logout
   * - Network errors display user-friendly messages
   * - Authentication failures clear session and redirect to login
   *
   * **Secondary Subscription:**
   * Monitors user changes for dynamic menu updates when user permissions change
   *
   * @param tokenService - Auth0 token management service
   * @param userService - User state and session management service
   * @param route - Angular router for navigation and redirects
   * @param dialogService - Modal dialog management service
   * @param userApiService - API service for user data and permissions
   * @param cdr - Change detection reference for performance optimization
   * @param authService - Auth0 authentication service
   */
  constructor(
    private readonly tokenService: TokenService,
    private route: Router,
    private dialogService: DialogService,
    private userApiService: UserApiService,
    private readonly cdr: ChangeDetectorRef,
    private authService: AuthService,
  ) {
    this.tokenService.accessTokenObservable.subscribe((token: string | null) => {
      if (!token) return;
      this.tokenService.userObservable
        .pipe(
          switchMap(user => {
            this.userService.setAuth({
              email: btoa(`${user?.email}`),
              authId: user?.sub,
            });
            this.username = user?.email;
            return this.userApiService.getUserDetails();
          }),
        )
        .subscribe({
          next: response => {
            if (response.data) {
              this.userService.setUser(response.data);
              this.fetchCountryIds(response.data);
              this.extractOutsourcingGroups(response.data);
              this.menus = MENUS;
              this.menus = this.filterMenuByUser(response.data);
              const privilegeMap = this.createPrivilegeMap(response.data);
              this.userService.setUserPrivilege(privilegeMap || {});
              this.dataLoaded$.next(true);

              // Check for stored redirect URL and navigate if found
              const redirectUrl = sessionStorage.getItem('auth_redirect_url');
              if (redirectUrl) {
                sessionStorage.removeItem('auth_redirect_url');
                this.route.navigateByUrl(redirectUrl);
              }

              this.cdr.markForCheck();
            }
            if (response.status === 'FAILURE') {
              this.errorMessage$.next({ message: response?.message ?? 'There was an error while fetching the data' });
              this.openDialog();
            }
          },
          error: err => {
            this.errorMessage$.next({ message: err?.message ?? 'Unknown error' });
            this.openDialog();
            this.cdr.markForCheck();
          },
        });
    });

    // Subscribe to user changes for menu filtering
    this.userService.user$.subscribe(user => {
      this.cdr.markForCheck();
      if (user && user.accessGroupList) {
        this.email = user.email;
        if (user.accessGroupList?.length === 0) {
          setTimeout(() => {
            if (this.errorDialogRef) {
              this.dialogService.openDialog(this.errorDialogRef);
            }
          });
          this.menus = [];
          this.isUserPriviledge = false;
        } else {
          this.menus = this.filterMenuByUser(user);
          const privilageMap = this.createPrivilegeMap(user);
          this.userService.setUserPrivilege(privilageMap || {});
        }
      }
    });
  }

  ngOnInit(): void {
    this.environmentName = environment.auth0.authorizationParams.environment;
  }

  /**
   * Creates a privilege map from user access groups for permission checking
   *
   * @param user - The user object containing access group information
   * @returns {ResourceType | undefined} A mapped structure of user privileges by resource and business line
   *
   * @example
   * ```typescript
   * const user = {
   *   accessGroupList: [
   *     { group: 'tyre-tc-view', view: true, insert: false, update: false, delete: false }
   *   ]
   * };
   * const privileges = this.createPrivilegeMap(user);
   * // Returns: { tyres: { tc: { view: true, insert: false, update: false, delete: false } } }
   * ```
   */
  createPrivilegeMap(user: User) {
    return user.accessGroupList?.reduce((acc: ResourceType, current) => {
      if (current?.group) {
        const parts = current.group.split('-');
        if (parts.length >= 2) {
          let groupName = parts[0].toLowerCase();
          const businessLineCode = parts[1].toLowerCase();
          if (groupName === 'tyre') groupName = 'tyres';
          if (groupName === 'vehicle') groupName = 'vehicles';

          const view = current.view || current.insert || current.update || current.delete;

          if (!acc[groupName]) {
            acc[groupName] = {};
          }

          if (!acc[groupName][businessLineCode]) {
            acc[groupName][businessLineCode] = {
              businessLineCode: businessLineCode,
              view: view,
              insert: current.insert || false,
              update: current.update || false,
              delete: current.delete || false,
            };
          } else {
            // Merge permissions
            const existing = acc[groupName][businessLineCode];
            acc[groupName][businessLineCode] = {
              businessLineCode: businessLineCode,
              view: existing.view || view,
              insert: existing.insert || current.insert,
              update: existing.update || current.update,
              delete: existing.delete || current.delete,
            };
          }
        }
      }
      return acc;
    }, {} as ResourceType);
  }

  /**
   * Filters menu items by business line based on user permissions
   *
   * @param user - The user object containing access permissions
   * @param menu - The menu item to check permissions for
   * @returns {boolean} True if user has access to the menu item
   *
   * @example
   * ```typescript
   * const hasAccess = this.filterByBusinessLine(user, menuItem);
   * if (hasAccess) {
   *   // Show menu item
   * }
   * ```
   */
  filterByBusinessLine = (user: User, menu: Menu | undefined): boolean => {
    if (!menu) return false;

    return (
      user?.accessGroupList?.some(group => {
        if (!group?.group) return false;

        const parts = group.group.split('-');
        if (parts.length < 2) return false;

        const groupName = parts[0].toLowerCase();
        const businessLineCode = parts[1].toLowerCase();

        return menu.groupName?.toLowerCase() === groupName && menu.businessLineCode?.toLowerCase() === businessLineCode;
      }) || false
    );
  };

  /**
   * Filters the entire menu structure based on user permissions
   * This method recursively filters menus and submenus to show only accessible items
   *
   * @param user - The user object containing access permissions
   * @returns {Menu[]} Array of filtered menu items that the user can access
   *
   * @example
   * ```typescript
   * const userMenus = this.filterMenuByUser(currentUser);
   * this.menus = userMenus;
   * ```
   */
  filterMenuByUser = (user: User): Menu[] => {
    const accessGroups = user?.accessGroupList || [];

    const filterMenu: Menu[] = this.menus.filter(menu => {
      return accessGroups.some(group => {
        const [groupResource] = group.group?.split('-') || [];
        return groupResource?.toLowerCase() === menu?.groupName?.toLowerCase();
      });
    });

    filterMenu.map(resourceMenu => {
      const subMenu = resourceMenu.submenus?.filter(subMenu => {
        const subSubMenu = subMenu?.submenus?.filter(subSubMenu => {
          return this.filterByBusinessLine(user, subSubMenu);
        });

        if (subMenu) {
          subMenu.submenus = subSubMenu;
        }
        return subMenu?.submenus?.length ? subMenu : this.filterByBusinessLine(user, subMenu);
      });
      resourceMenu.submenus = subMenu;
    });

    return filterMenu;
  };

  /**
   * TrackBy function for Angular ngFor optimization when rendering menus
   *
   * @param _ - Index parameter (unused)
   * @param menu - Menu item to track
   * @returns {string | undefined} Unique identifier for the menu item
   */
  trackMenuBy = (_: number, menu: Menu | undefined) => menu?.id;

  /**
   * Initiates the user logout process through Auth0
   * Clears local session data after successful logout
   *
   * @example
   * ```typescript
   * // Called when user clicks logout button
   * this.doSignOut();
   * ```
   */
  doSignOut = () => {
    this.tokenService.logout().subscribe(() => {
      this.clearSession();
    });
  };

  /**
   * Clears all session data including tokens and user information
   * This method is called during logout or session expiration
   *
   * @example
   * ```typescript
   * // Clear session on error or logout
   * this.clearSession();
   * ```
   */
  clearSession = () => {
    this.tokenService.clearTokens(); // This also clears auth_redirect_url
    this.userService.clear();
    //window.location.reload();
  };

  /**
   * Initiates Auth0 login with redirect flow
   * Called when user needs to authenticate
   *
   * @example
   * ```typescript
   * // Called when user clicks login button
   * this.loginWithRedirect();
   * ```
   */
  loginWithRedirect() {
    this.tokenService.loginWithRedirect().subscribe();
  }

  /**
   * Opens the error dialog
   * Automatically signs out user when dialog is closed
   *
   * @example
   * ```typescript
   * // Called when errors occur or authentication fails
   * this.openDialog();
   * ```
   */
  openDialog() {
    if (this.errorDialogRef) {
      this.dialogService.openDialog(this.errorDialogRef).subscribe(() => {
        this.doSignOut();
      });
    }
  }

  /**
   * Closes all open dialogs
   *
   * @example
   * ```typescript
   * // Close dialogs programmatically
   * this.closeDialog();
   * ```
   */
  closeDialog() {
    this.dialogService.closeAll();
  }

  /**
   * Fetches country IDs based on user's regional access groups
   *
   * @description
   * Processes access groups that start with 'VH-MR-' pattern to determine
   * regional access permissions. Extracts region codes and fetches corresponding
   * country IDs from the API. Sets the country ID in user service for regional filtering.
   *
   * @param user - The user object containing access group information
   *
   * @example
   * ```typescript
   * // User with regional access: ['VH-MR-US', 'VH-MR-EU']
   * this.fetchCountryIds(user);
   * // Extracts: ['US', 'EU'] and fetches corresponding country IDs
   * ```
   *
   * @throws Will display error dialog if API request fails
   */
  fetchCountryIds(user: User) {
    const regionCodeList: string[] =
      user?.accessGroupList
        ?.filter((group: AccessGroupList) => group.group && group.group.startsWith('VH-MR-'))
        ?.map((group: AccessGroupList) => (group.group ? group.group.replace('VH-MR-', '') : '')) ?? [];
    if (regionCodeList.length > 0) {
      this.userApiService.fetchCountryIds(regionCodeList).subscribe(response => {
        if (response.status === 'ERROR') {
          this.errorMessage$.next({ message: response.message ?? 'Unknown error' });
          this.openDialog();
        }
        this.userService.setCountryId(response.message);
      });
    }
  }

  /**
   * Extracts and processes outsourcing groups from user access groups
   *
   * @description
   * Processes access groups that start with 'OUTSOURCE-' pattern to determine
   * outsourcing permissions. Removes the prefix and formats group names by
   * replacing hyphens with spaces. Updates the user object with processed
   * outsourcing groups for business logic filtering.
   *
   * @param user - The user object containing access group information
   *
   * @example
   * ```typescript
   * // User with outsourcing access: ['OUTSOURCE-CORE-DATA', 'OUTSOURCE-EXTERNAL-VENDOR']
   * this.extractOutsourcingGroups(user);
   * // Results in: ['CORE DATA', 'EXTERNAL VENDOR']
   * // Updated user object includes: { ...user, outsourcingGroups: [...] }
   * ```
   */
  extractOutsourcingGroups(user: User) {
    const list = user?.accessGroupList
      ?.filter((group: AccessGroupList) => group.group && group.group.startsWith('OUTSOURCE-'))
      ?.map((group: AccessGroupList) => (group.group ? group.group.replace('OUTSOURCE-', '') : ''))
      ?.map((group: string) => {
        const groupWithoutPrefix = group.replaceAll("-", " ");
        return groupWithoutPrefix;
      }) ?? [];
      this.userService.setUser({...user, outsourcingGroups: list});
  }
}
